/**
 * Single source of truth for carton label print formatting.
 *
 * The Custom Labels page persists the user's chosen format here (localStorage).
 * All other label print flows — single carton, Quick Weigh auto-print, bulk
 * prints from Inventory / Shift Stock — read this format so every label
 * looks the same everywhere.
 */

export const FONT_FAMILIES = [
  { key: "system",    label: "System (default)",   css: "system-ui, -apple-system, sans-serif" },
  { key: "sans",      label: "Sans-serif (Inter)", css: "'Inter', 'Helvetica Neue', Arial, sans-serif" },
  { key: "serif",     label: "Serif (Georgia)",    css: "Georgia, 'Times New Roman', serif" },
  { key: "mono",      label: "Monospace",          css: "'IBM Plex Mono', Menlo, Consolas, monospace" },
  { key: "condensed", label: "Condensed",          css: "'Arial Narrow', 'Roboto Condensed', sans-serif" },
];

// Global font size — one numeric dropdown from 10 to 96 px, replacing the
// old S / M / L / XL multipliers. Each entry's `mult` is derived from the
// legacy default gross-weight size (25 px) so an entry labelled "25 px"
// behaves exactly like the old "Medium (1.0×)" preset.
export const FONT_SCALES = Array.from({ length: 96 - 10 + 1 }, (_, i) => {
  const px = i + 10;
  return { key: String(px), label: `${px} px`, mult: px / 25 };
});

// Legacy → new mapping so users who saved "s"/"m"/"l"/"xl" upgrade cleanly.
const LEGACY_FONT_SCALE_MAP = { s: "21", m: "25", l: "30", xl: "38" };

// Numeric font-size presets exposed on the Custom Labels page for per-item
// overrides. Every integer 10 → 96 px so operators have fine-grained control
// without needing to type into a free-text input.
export const FONT_SIZE_PRESETS_PX = Array.from({ length: 96 - 10 + 1 }, (_, i) => i + 10);

// Per-field default value-text sizes (unscaled). LabelTile uses these when
// no explicit per-field override is set. Small helper labels above each value
// keep their fixed relative sizing and follow the global fontScale.
export const DEFAULT_FIELD_SIZES_PX = Object.freeze({
  box_no:      17,
  gross:       25,
  net:         13,
  dc_net:      13,
  sku:         19,
  spec:        11,
  produced_at: 9,
  company:     7,
  custom:      10,
});

export const SIZE_PRESETS = [
  { key: "50x50",   label: "50 × 50 mm (single)",         pageW: 50,  pageH: 50,  perPage: 1 },
  { key: "100x100", label: "100 × 100 mm (single large)", pageW: 100, pageH: 100, perPage: 1 },
  { key: "104x50",  label: "104 × 50 mm (2 per sheet)",   pageW: 104, pageH: 50,  perPage: 2 },
  { key: "100x150", label: "100 × 150 mm (packing)",      pageW: 100, pageH: 150, perPage: 1 },
  { key: "a4grid",  label: "A4 sheet — grid",             pageW: 210, pageH: 297, perPage: 6 },
];

export const FIELD_OPTS = [
  { key: "box_no",      label: "Carton No" },
  { key: "gross",       label: "Gross Weight" },
  { key: "net",         label: "Net Weight" },
  { key: "dc_net",      label: "Net Wt (as per Delivery Challan)" },
  { key: "sku",         label: "SKU" },
  { key: "spec",        label: "Spec (width/µ/roll)" },
  { key: "produced_at", label: "Production Date" },
  { key: "company",     label: "Company Name" },
  { key: "custom",      label: "Custom Free Text" },
];

const STORAGE_KEY = "wpl.labelFormat.v1";

// Defaults match the legacy 2-up 104×50 layout so existing users see no
// visible change until they save something on the Custom Labels page.
export const DEFAULT_LABEL_FORMAT = Object.freeze({
  size:       "104x50",
  fontFamily: "system",
  fontScale:  "25",            // 25 px ≈ legacy Medium 1.0× multiplier
  fields:     ["box_no", "gross", "sku"],
  customText: "Handle with care",
  fieldSizes: {}, // per-field numeric px overrides, e.g. { box_no: 24, gross: 32 }
});

export function loadLabelFormat() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return { ...DEFAULT_LABEL_FORMAT, fieldSizes: { ...DEFAULT_LABEL_FORMAT.fieldSizes } };
    const parsed = JSON.parse(raw);
    // Migrate legacy S/M/L/XL scale keys to their numeric equivalents.
    const scaleRaw = typeof parsed.fontScale === "string" ? parsed.fontScale : DEFAULT_LABEL_FORMAT.fontScale;
    const scale = LEGACY_FONT_SCALE_MAP[scaleRaw] || scaleRaw;
    return {
      size:       typeof parsed.size === "string" ? parsed.size : DEFAULT_LABEL_FORMAT.size,
      fontFamily: typeof parsed.fontFamily === "string" ? parsed.fontFamily : DEFAULT_LABEL_FORMAT.fontFamily,
      fontScale:  scale,
      fields:     Array.isArray(parsed.fields) && parsed.fields.length > 0 ? parsed.fields : [...DEFAULT_LABEL_FORMAT.fields],
      customText: typeof parsed.customText === "string" ? parsed.customText : DEFAULT_LABEL_FORMAT.customText,
      fieldSizes: parsed.fieldSizes && typeof parsed.fieldSizes === "object" ? { ...parsed.fieldSizes } : {},
    };
  } catch {
    return { ...DEFAULT_LABEL_FORMAT, fieldSizes: {} };
  }
}

export function saveLabelFormat(fmt) {
  try {
    const clean = {
      size:       fmt.size,
      fontFamily: fmt.fontFamily,
      fontScale:  fmt.fontScale,
      fields:     Array.isArray(fmt.fields) ? fmt.fields : [...(fmt.fields || [])],
      customText: fmt.customText || "",
      fieldSizes: fmt.fieldSizes && typeof fmt.fieldSizes === "object" ? { ...fmt.fieldSizes } : {},
    };
    localStorage.setItem(STORAGE_KEY, JSON.stringify(clean));
  } catch { /* localStorage full or blocked; silently ignore */ }
}

// Convenience lookups so callers don't repeat find() boilerplate.
export function resolveFormat(fmt) {
  const preset = SIZE_PRESETS.find((p) => p.key === fmt.size) || SIZE_PRESETS[2];
  const family = FONT_FAMILIES.find((f) => f.key === fmt.fontFamily) || FONT_FAMILIES[0];
  // Migrate any legacy S/M/L/XL key on the fly.
  const scaleKey = LEGACY_FONT_SCALE_MAP[fmt.fontScale] || fmt.fontScale;
  const scale = FONT_SCALES.find((s) => s.key === scaleKey)
    || FONT_SCALES.find((s) => s.key === "25")
    || FONT_SCALES[0];
  const fieldSet = new Set(Array.isArray(fmt.fields) ? fmt.fields : []);
  const fieldSizes = fmt.fieldSizes && typeof fmt.fieldSizes === "object" ? fmt.fieldSizes : {};
  return { preset, family, scale, fieldSet, customText: fmt.customText || "", fieldSizes };
}

/**
 * Returns the effective px size for a field's value text.
 * Priority:  explicit per-field override → default base × global scale.
 */
export function effectiveFieldSize(key, fieldSizes, scaleMult) {
  const raw = fieldSizes && fieldSizes[key];
  if (typeof raw === "number" && raw > 0) return raw;
  const base = DEFAULT_FIELD_SIZES_PX[key] || 12;
  return base * (scaleMult || 1);
}
